import * as React from "react";
import { Card, Divider } from "antd";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { defaultValues, toSelectOptions } from "../../../services/defaults";
import { formSet, formSetValue, formTriggerValidation, useCpFormValue } from "../../gvc/testNewForm/utils";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpSwitch } from "../../gvc/testNewForm/CpSwitch";
import { CpInput } from "../../gvc/testNewForm/CpInput";
import { CpSelect } from "../../gvc/testNewForm/CpSelect";
import { CpInputNumber } from "../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { CpErrorWarningArrayIcon } from "../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpLabelTooltip } from "../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../gvc/testNewForm/CpTooltip";
import { store } from "../../../store";

interface Props {}
export const WorkloadAdvanced: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();
  const type = useCpFormValue(`type`);

  const extras = useCpFormValue(`extras`);
  const rolloutOptionsEnabled = useCpFormValue(`rolloutOptions.isEnabled`);

  return (
    <div className="cpln-form-section cpln-ant-card-container">
      <Card>
        <CardTitle>
          Advanced Settings
          <CpErrorWarningArrayIcon
            names={[
              "supportDynamicTags",
              "rolloutOptions.isEnabled",
              "rolloutOptions",
              "securityOptions",
              "extras",
              "requestRetryPolicy",
            ]}
          />
        </CardTitle>
        <CardContent>
          <CpFormItem>
            <CpLabelTooltip>
              <CpLabel name={`supportDynamicTags`}>Support Dynamic Tags</CpLabel>
              <CpSwitch name={`supportDynamicTags`} />
              <CpTooltip tooltipProps={{ title: `Allows to use image tags such as :latest` }} />
            </CpLabelTooltip>
          </CpFormItem>
          <CpFormItem>
            <div>Extras</div>
            <CodeEditor
              language="yaml"
              options={{ style: { fontSize: 14 } }}
              value={extras}
              setValue={(value) => {
                formSetValue(id, `extras`, value);
              }}
              onChange={() => {
                const name = "extras";
                formSet(id, `fieldStates.${name}.modified`, true);

                const form = store.getState().form.instances[id];
                const onChangeCounter = form.meta.onChangeCounter;
                formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
              }}
              onFocus={() => {
                const name = "extras";
                formSet(id, `fieldStates.${name}.focused`, true);
                formSet(id, `fieldStates.${name}.visited`, true);
              }}
              onBlur={() => {
                const name = "extras";
                formSet(id, `fieldStates.${name}.focused`, false);
                formSet(id, `fieldStates.${name}.touched`, true);

                const form = store.getState().form.instances[id];
                const onBlurCounter = form.meta.onBlurCounter;
                formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
              }}
            />
            <CpErrorWarning name={`extras`} />
          </CpFormItem>
          <Divider orientation={`left`} orientationMargin={0}>
            Security Options
          </Divider>
          <CpFormItem>
            <CpLabelTooltip>
              <CpLabel name={`securityOptions.filesystemGroupId`}>File System Group ID</CpLabel>
              <CpTooltip tooltipProps={{ title: "The group id assigned to any mounted volume" }} />
            </CpLabelTooltip>
            <CpInputNumber name={`securityOptions.filesystemGroupId`} />
            <CpErrorWarning name={`securityOptions.filesystemGroupId`} />
          </CpFormItem>
          <CpFormItem>
            <CpLabelTooltip>
              <CpLabel name={`securityOptions.runAsUser`}>Run as User</CpLabel>
              <CpTooltip tooltipProps={{ title: "The user id assigned to all container processes" }} />
            </CpLabelTooltip>
            <CpInputNumber name={`securityOptions.runAsUser`} />
            <CpErrorWarning name={`securityOptions.runAsUser`} />
          </CpFormItem>
          {type !== "cron" ? (
            <>
              <Divider orientation={`left`} orientationMargin={0}>
                Rollout Options
              </Divider>
              <div>
                <CpFormItem>
                  <CpLabelTooltip>
                    <CpLabel name={`rolloutOptions.isEnabled`}>Enabled</CpLabel>
                    <CpSwitch name={`rolloutOptions.isEnabled`} />
                  </CpLabelTooltip>
                </CpFormItem>
                {rolloutOptionsEnabled ? (
                  <>
                    <CpFormItem>
                      <CpLabel name={`rolloutOptions.scalingPolicy`}>Scaling Policy</CpLabel>
                      <CpSelect
                        style={{ width: 300 }}
                        name={`rolloutOptions.scalingPolicy`}
                        options={[
                          { label: "OrderedReady", value: "OrderedReady" },
                          { label: "Parallel", value: "Parallel" },
                        ]}
                      />
                      <CpErrorWarning name={`rolloutOptions.scalingPolicy`} />
                    </CpFormItem>
                    <CpFormItem>
                      <CpLabelTooltip>
                        <CpLabel name={`rolloutOptions.maxSurgeReplicas`}>Max Surge Replicas</CpLabel>
                        <CpTooltip
                          tooltipProps={{
                            title:
                              "Valid values include: integer values, and floating point percentages e.g. 2, 20%, 10.5%",
                          }}
                        />
                      </CpLabelTooltip>
                      <CpInput style={{ width: 300 }} name={`rolloutOptions.maxSurgeReplicas`} />
                      <CpErrorWarning name={`rolloutOptions.maxSurgeReplicas`} />
                    </CpFormItem>
                    <CpFormItem>
                      <CpLabelTooltip>
                        <CpLabel name={`rolloutOptions.minReadySeconds`}>Min Ready Seconds</CpLabel>
                        <CpTooltip
                          tooltipProps={{
                            title:
                              "The minimum number of seconds a container must run without crashing to be considered available",
                          }}
                        />
                      </CpLabelTooltip>
                      <CpInput style={{ width: 300 }} name={`rolloutOptions.minReadySeconds`} />
                      <CpErrorWarning name={`rolloutOptions.minReadySeconds`} />
                    </CpFormItem>
                    <CpFormItem>
                      <CpLabelTooltip>
                        <CpLabel name={`rolloutOptions.terminationGracePeriodSeconds`}>
                          Termination Grace Period Seconds
                        </CpLabel>
                        <CpTooltip
                          tooltipProps={{
                            title: `The amount of time in seconds a workload has to gracefully terminate before forcefully terminating it. This includes the time it takes for the preStop hook to run.`,
                          }}
                        />
                      </CpLabelTooltip>
                      <CpInput style={{ width: 300 }} name={`rolloutOptions.terminationGracePeriodSeconds`} placeholder="90" />
                      <CpErrorWarning name={`rolloutOptions.terminationGracePeriodSeconds`} />
                    </CpFormItem>
                    {type === "stateful" ? null : (
                      <CpFormItem>
                        <CpLabelTooltip>
                          <CpLabel name={`rolloutOptions.maxUnavailableReplicas`}>Max Unavailable Replicas</CpLabel>
                          <CpTooltip
                            tooltipProps={{
                              title:
                                "Valid values include: integer values, and floating point percentages e.g. 2, 20%, 10.5%",
                            }}
                          />
                        </CpLabelTooltip>
                        <CpInput style={{ width: 300 }} name={`rolloutOptions.maxUnavailableReplicas`} />
                        <CpErrorWarning name={`rolloutOptions.maxUnavailableReplicas`} />
                      </CpFormItem>
                    )}
                  </>
                ) : null}
              </div>
            </>
          ) : null}
          <Divider orientation={`left`} orientationMargin={0}>
            Request Retry Policy
          </Divider>
          <CpFormItem>
            <CpLabel name={`requestRetryPolicy.attempts`}>Attempts</CpLabel>
            <CpInputNumber name={`requestRetryPolicy.attempts`} />
            <CpErrorWarning name={`requestRetryPolicy.attempts`} />
          </CpFormItem>
          <CpFormItem>
            <CpLabel name={`requestRetryPolicy.retryOn`}>Retry On</CpLabel>
            <CpSelect
              name={`requestRetryPolicy.retryOn`}
              mode={"tags"}
              options={toSelectOptions(defaultValues.workload.requestRetryPolicy.retryOn.options)}
              style={{ maxWidth: 900 }}
            />
            <CpErrorWarning name={`requestRetryPolicy.retryOn`} />
          </CpFormItem>
        </CardContent>
      </Card>
    </div>
  );
};
